var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Second-factor helpers, shared by the enrolment page and the profile pages that
 * reauthenticate.
 *
 * Same compat/modular split as the sign-in panel: everything stays on the compat
 * instance except TotpMultiFactorGenerator, which is not registered on the compat
 * auth namespace. It has only static methods and never touches an Auth instance,
 * so mixing is safe.
 *
 * The authority on whether 2FA is on is always the enrolled-factor list on the
 * Firebase user — never a mirrored flag. firestore.rules lets a user write any
 * field on their own users/{uid} document, so a mirror could disagree with reality
 * and could be forged.
 */
import firebase from "firebase/compat/app";
import 'firebase/compat/auth';
import { TotpMultiFactorGenerator, FactorId } from 'firebase/auth';
import { FirebaseAuth } from '../../components/FirebaseAuth/firebase';
import { authErrorMessage, isKnownAuthError } from '../authErrors';
export var TOTP_FACTOR_ID = FactorId.TOTP;
var currentUser = function () { return FirebaseAuth.auth().currentUser; };
/** Enrolled TOTP factors as currently known to the client. */
export var getTotpFactors = function () {
    var user = currentUser();
    if (!user) {
        return [];
    }
    return user.multiFactor.enrolledFactors.filter(function (f) { return f.factorId === TOTP_FACTOR_ID; });
};
/**
 * Re-reads the user from the server first. AuthContext only refreshes on
 * onAuthStateChanged, which does not fire on enrol or unenrol, so anything
 * displaying 2FA state has to ask explicitly.
 */
export var refreshTotpFactors = function () { return __awaiter(void 0, void 0, Promise, function () {
    var user;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                user = currentUser();
                if (!user) {
                    return [2 /*return*/, []];
                }
                return [4 /*yield*/, user.reload()];
            case 1:
                _a.sent();
                return [2 /*return*/, getTotpFactors()];
        }
    });
}); };
/**
 * Which sign-in method to reauthenticate with.
 *
 * Prefers 'password' wherever it appears rather than taking providerData[0]: a
 * user who linked Google onto a password account can have either order, and a
 * password prompt is less disruptive than a popup.
 */
export var primaryProviderId = function (user) {
    var _a;
    if (!user.providerData || user.providerData.length === 0) {
        return null;
    }
    var hasPassword = user.providerData.some(function (p) { return (p === null || p === void 0 ? void 0 : p.providerId) === 'password'; });
    if (hasPassword) {
        return 'password';
    }
    return ((_a = user.providerData[0]) === null || _a === void 0 ? void 0 : _a.providerId) || null;
};
/** True when reauthentication needs a typed password rather than a popup. */
export var needsPassword = function (user) {
    return primaryProviderId(user) === 'password';
};
/**
 * Proves the visitor is still who they say they are, for an operation Firebase
 * considers sensitive.
 *
 * Popup rather than redirect, for the same reason sign-in uses popup: on localhost
 * authDomain falls back to *.firebaseapp.com, cross-origin with the app, and
 * browsers partition the third-party storage a redirect flow needs.
 */
export var reauthenticate = function (user, password) {
    var providerId = primaryProviderId(user);
    if (providerId === 'password') {
        var credential = firebase.auth.EmailAuthProvider.credential(user.email || '', password || '');
        return user.reauthenticateWithCredential(credential);
    }
    if (providerId === 'google.com') {
        return user.reauthenticateWithPopup(new firebase.auth.GoogleAuthProvider());
    }
    if (providerId === 'microsoft.com') {
        return user.reauthenticateWithPopup(new firebase.auth.OAuthProvider('microsoft.com'));
    }
    return Promise.reject(new Error("We can't re-verify this sign-in method automatically. Please sign out and back in, then try again."));
};
/** Completes a challenge raised by a sensitive operation on an enrolled account. */
export var resolveTotpChallenge = function (resolver, code) {
    var hint = resolver.hints.find(function (h) { return h.factorId === TOTP_FACTOR_ID; }) || resolver.hints[0];
    var assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    return resolver.resolveSignIn(assertion);
};
export var isMfaRequired = function (err) {
    return (err === null || err === void 0 ? void 0 : err.code) === 'auth/multi-factor-auth-required';
};
export var isRecentLoginRequired = function (err) {
    return (err === null || err === void 0 ? void 0 : err.code) === 'auth/requires-recent-login';
};
/** Codes the caller should show against the input rather than as a page error. */
export var isBadCode = function (err) {
    return (err === null || err === void 0 ? void 0 : err.code) === 'auth/invalid-verification-code' ||
        (err === null || err === void 0 ? void 0 : err.code) === 'auth/missing-verification-code' ||
        (err === null || err === void 0 ? void 0 : err.code) === 'auth/invalid-verification-id' ||
        (err === null || err === void 0 ? void 0 : err.code) === 'auth/code-expired';
};
var MESSAGES = {
    'auth/unverified-email': 'You need to verify your email address before you can turn on two-factor authentication.',
    'auth/invalid-verification-code': "That code didn't match. Check that your phone's clock is set automatically, then enter the current 6-digit code.",
    'auth/missing-verification-code': 'Enter the 6-digit code from your authenticator app.',
    'auth/code-expired': 'That code has expired. Enter the current one from your authenticator app.',
    'auth/maximum-second-factor-count-exceeded': "You've already added the maximum number of second factors to this account. Remove one before adding another.",
    'auth/second-factor-already-in-use': 'That authenticator app is already set up on this account.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
    'auth/wrong-password': 'Incorrect password, authentication failed.',
    'auth/invalid-credential': 'Incorrect password, authentication failed.',
    'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
    'auth/popup-closed-by-user': 'The sign-in window closed before you finished. Try again.',
    'auth/cancelled-popup-request': 'The sign-in window closed before you finished. Try again.',
};
/**
 * Message for a failure during an MFA operation — enrol, unenrol, or a challenge.
 *
 * Three tiers, in order, and the order matters. The "isn't available" text at the
 * bottom describes one specific situation: a project that has not been upgraded to
 * Identity Platform, or that has TOTP switched off, which fails with
 * auth/internal-error carrying raw JSON in .message. Reaching for it too early
 * makes ordinary failures — a dropped connection, a wrong password — claim the
 * feature is switched off, which sends the user to support for no reason.
 *
 * So: MFA-specific codes first, then the shared map of general auth codes, and only
 * then the availability message. Since that last tier hides the real cause, log it
 * so a developer can still find it.
 */
export var mfaErrorMessage = function (err) {
    var code = err === null || err === void 0 ? void 0 : err.code;
    if (code && Object.prototype.hasOwnProperty.call(MESSAGES, code)) {
        return MESSAGES[code];
    }
    if (code && isKnownAuthError(code)) {
        return authErrorMessage(code) || 'That did not work. Please try again.';
    }
    // eslint-disable-next-line no-console
    console.error('Two-factor operation failed', err);
    return "Two-factor authentication isn't available on your account yet. " +
        'Please contact support@ltiaas.com and we\'ll get it enabled for you.';
};
/** Starts an enrolment and returns the secret to show as a QR code. */
export var startTotpEnrollment = function (user) { return __awaiter(void 0, void 0, void 0, function () {
    var session;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, user.multiFactor.getSession()];
            case 1:
                session = _a.sent();
                return [2 /*return*/, TotpMultiFactorGenerator.generateSecret(session)];
        }
    });
}); };
/** Finishes an enrolment with the code the visitor read from their app. */
export var finishTotpEnrollment = function (user, secret, code) {
    var assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    return user.multiFactor.enroll(assertion, 'Authenticator app');
};
export var unenrollTotp = function (user, factor) {
    return user.multiFactor.unenroll(factor);
};
